import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Navigation, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';

import style from './profile-card.module.css';
import { TProfile } from '../../types';

interface ProfileCardProps {
    profile: TProfile | null;
    onApprove?: () => void;
    onHide?: () => void;
    isProcessing?: boolean;
}

const formatEventCountWord = (count: number) => {
    const mod100 = count % 100;
    const mod10 = count % 10;

    if (mod100 >= 11 && mod100 <= 14) return 'событий';
    if (mod10 === 1) return 'событие';
    if (mod10 >= 2 && mod10 <= 4) return 'события';

    return 'событий';
};

const getMemberStatusText = (profile: TProfile) => {
    if (!profile.member_status_label) return null;

    const count = profile.member_status_events_count ?? profile.events_visit ?? 0;

    return {
        label: profile.member_status_label,
        countText: `${count} ${formatEventCountWord(count)}`,
    };
};

const ProfileCard: React.FC<ProfileCardProps> = ({
    profile,
    onApprove,
    onHide,
    isProcessing = false,
}) => {
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [panelMetrics, setPanelMetrics] = useState<{
        left: number;
        width: number;
        minHeight: number;
        maxHeight: number;
        closeLeft: number;
    } | null>(null);
    const infoBoxRef = useRef<HTMLDivElement | null>(null);
    const infoButtonRef = useRef<HTMLButtonElement | null>(null);
    const modalRoot = typeof document !== 'undefined' ? document.getElementById('react-modals') : null;

    useEffect(() => {
        if (!isInfoOpen) {
            setPanelMetrics(null);
            return;
        }

        const updateMetrics = () => {
            const infoBox = infoBoxRef.current;
            const infoButton = infoButtonRef.current;

            if (!infoBox || !infoButton) {
                return;
            }

            const infoBoxRect = infoBox.getBoundingClientRect();
            const buttonRect = infoButton.getBoundingClientRect();
            const mainRect = infoBox.closest('main')?.getBoundingClientRect();
            const panelLeft = mainRect?.left ?? infoBoxRect.left;
            const panelWidth = mainRect?.width ?? infoBoxRect.width;
            const closeButtonWidth = 44;
            const buttonCenter = buttonRect.left - panelLeft + (buttonRect.width / 2);

            setPanelMetrics({
                left: panelLeft,
                width: panelWidth,
                minHeight: Math.max(window.innerHeight - infoBoxRect.top, 240),
                maxHeight: Math.max(window.innerHeight - 24, 240),
                closeLeft: Math.max(8, Math.min(buttonCenter - (closeButtonWidth / 2), panelWidth - closeButtonWidth - 8)),
            });
        };

        updateMetrics();

        window.addEventListener('resize', updateMetrics);

        return () => {
            window.removeEventListener('resize', updateMetrics);
        };
    }, [isInfoOpen]);

    if (!profile) return null;

    const visitStr = profile.gender === 'male' ? 'Посетил' : 'Посетила';
    const heading = profile.hide_year ? profile.name : `${profile.name}, ${profile.age}`;
    const relationshipText = !profile.hide_relationship_status && profile.relationship_status_str
        ? profile.relationship_status_str
        : 'Не указано';
    const memberStatusText = getMemberStatusText(profile);
    const lifestyles = profile.lifestyles ?? [];

    return (
        <>
            <div className={style.card}>
                <Swiper
                    modules={[Navigation, A11y]}
                    spaceBetween={0}
                    slidesPerView={1}
                    navigation
                >
                    {profile.photos.map(photo => {
                        const baseUrl = photo.image_urls?.profile || photo.url || '';
                        const cacheBustedUrl = photo.updated_at
                            ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(photo.updated_at)}`
                            : baseUrl;

                        return (
                            <SwiperSlide key={photo.id}>
                                <div className={style.image}>
                                    <img src={cacheBustedUrl} alt="" />
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>

                <div ref={infoBoxRef} className={style.infoBox}>
                    <div className={style.identity}>
                        <div className={style.info}>
                            <span className={style.name}>{profile.name}</span>
                            {!profile.hide_year && (<span className={style.age}>, {profile.age}</span>)}
                        </div>
                        {memberStatusText && (
                            <div className={style.memberStatus}>
                                <span>Статус: </span>
                                <span className={style.memberStatusLabel}>{memberStatusText.label}</span>
                                <span> ({memberStatusText.countText})</span>
                            </div>
                        )}
                    </div>
                    <div>
                        <button
                            ref={infoButtonRef}
                            type="button"
                            className={style.infoButton}
                            onClick={() => setIsInfoOpen(true)}
                            aria-expanded={isInfoOpen}
                            aria-label="Показать дополнительную информацию"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="w-9 h-9" viewBox="0 0 16 16">
                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                            </svg>
                        </button>
                    </div>
                </div>

            </div>

            {onHide && onApprove && (
                <div className={style.actions}>
                    <button
                        type="button"
                        className={style.hideButton}
                        onClick={onHide}
                        disabled={isProcessing}
                    >
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path d="M1.07983 20.7205L20.0565 1.04102" stroke="currentColor" strokeWidth="3" />
                            <path d="M1.78271 1.74365L20.0565 20.0174" stroke="currentColor" strokeWidth="3" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className={style.approveButton}
                        onClick={onApprove}
                        disabled={isProcessing}
                    >
                        СВОЙ!
                    </button>
                </div>
            )}

            {profile.events_visit > 0 && (
                <div className={style.visitBlock}>
                    {visitStr} мероприятий:{' '}
                    <span className={style.visitCount}>
                        {profile.events_visit}
                    </span>
                </div>
            )}

            {isInfoOpen && modalRoot && ReactDOM.createPortal(
                <div className={style.detailsRoot}>
                    <div className={style.detailsOverlay} />

                    <div
                        className={style.detailsPanel}
                        aria-hidden={!isInfoOpen}
                        style={panelMetrics ? {
                            left: `${panelMetrics.left}px`,
                            width: `${panelMetrics.width}px`,
                            minHeight: `${panelMetrics.minHeight}px`,
                            maxHeight: `${panelMetrics.maxHeight}px`,
                        } : undefined}
                    >
                        <button
                            type="button"
                            className={style.detailsClose}
                            onClick={() => setIsInfoOpen(false)}
                            aria-label="Скрыть дополнительную информацию"
                            style={panelMetrics ? { left: `${panelMetrics.closeLeft}px` } : undefined}
                        >
                            <span className={style.detailsCloseIcon}>

                                <svg width="15" height="20" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6.65685 19.7071C7.04737 20.0976 7.68054 20.0976 8.07106 19.7071L14.435 13.3431C14.8255 12.9526 14.8255 12.3195 14.435 11.9289C14.0445 11.5384 13.4113 11.5384 13.0208 11.9289L7.36395 17.5858L1.7071 11.9289C1.31657 11.5384 0.683409 11.5384 0.292885 11.9289C-0.0976396 12.3195 -0.0976396 12.9526 0.292885 13.3431L6.65685 19.7071ZM7.36395 0L6.36395 0L6.36395 19H7.36395H8.36395L8.36395 0L7.36395 0Z" fill="currentColor" />
                                </svg>

                            </span>
                        </button>

                        <div className={style.detailsContent}>
                            <div className={style.divider}>
                                <div className={style.detailsHeading}>{heading}</div>
                                {memberStatusText && (
                                    <div className={style.detailsMemberStatus}>
                                        <span>Статус: </span>
                                        <span className={style.memberStatusLabel}>{memberStatusText.label}</span>
                                        <span> ({memberStatusText.countText})</span>
                                    </div>
                                )}

                                {!profile.hide_relationship_status && (
                                    <div className={style.infoBlockRelationship}>
                                        <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M2.125 3.54167C2.125 1.58566 3.71066 0 5.66667 0C7.62267 0 9.20833 1.58566 9.20833 3.54167C9.20833 5.49767 7.62267 7.08333 5.66667 7.08333C3.71066 7.08333 2.125 5.49767 2.125 3.54167ZM5.66667 5.66667C6.84027 5.66667 7.79167 4.71527 7.79167 3.54167C7.79167 2.36806 6.84027 1.41667 5.66667 1.41667C4.49306 1.41667 3.54167 2.36806 3.54167 3.54167C3.54167 4.71527 4.49306 5.66667 5.66667 5.66667Z" fill="black" fill-opacity="0.5" />
                                            <path d="M1.65973 9.45139C0.597022 10.5141 0 11.9554 0 13.4583H1.41667C1.41667 12.3312 1.86443 11.2502 2.66146 10.4531C3.45849 9.6561 4.5395 9.20833 5.66667 9.20833C6.79384 9.20833 7.87484 9.6561 8.67187 10.4531C9.4689 11.2502 9.91667 12.3312 9.91667 13.4583H11.3333C11.3333 11.9554 10.7363 10.5141 9.67361 9.45139C8.6109 8.38869 7.16956 7.79167 5.66667 7.79167C4.16377 7.79167 2.72243 8.38869 1.65973 9.45139Z" fill="black" fill-opacity="0.5" />
                                        </svg>
                                        {relationshipText}
                                    </div>
                                )}
                            </div>

                            <div className={style.infoBlock}>
                                <div className={style.infoTitle}>В поиске</div>
                                {profile.goals.length > 0 ? (
                                    <div className={style.infoItems}>
                                        {profile.goals.map(goal => (
                                            <div key={goal.id} className={style.infoItem}>
                                                {goal.title}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={style.detailsEmpty}>Не указано</div>
                                )}
                            </div>

                            <div className={style.infoBlock}>
                                <div className={style.infoTitle}>Интересы</div>
                                {profile.interests.length > 0 ? (
                                    <div className={style.infoItems}>
                                        {profile.interests.map(interest => (
                                            <div key={interest.id} className={style.infoItem}>
                                                {interest.title}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={style.detailsEmpty}>Не указано</div>
                                )}
                            </div>

                            <div className={style.infoBlock}>
                                <div className={style.infoTitle}>Образ жизни</div>
                                {lifestyles.length > 0 ? (
                                    <div className={style.infoItems}>
                                        {lifestyles.map(lifestyle => (
                                            <div key={lifestyle.id} className={style.infoItem}>
                                                {lifestyle.title}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className={style.detailsEmpty}>Не указано</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>,
                modalRoot,
            )}
        </>
    );
};

export default ProfileCard;
